import React, { useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import moment from 'moment';
import WarningIcon from '@material-ui/icons/ErrorOutline';

import CaseShippingInfoModal from './CaseShippingInfoModal';
import ModalSelectInput from '../../shared/modal-select/ModalSelectInput';
import Input from '../../shared/Input';

import { getCase, simpleUpdateCase } from '../../../actions/casesActions';

import InfoIcon from '../../../assets/icons/InfoIcon';
import CheckIcon from '../../../assets/icons/CheckIcon';

import { fromMomentToTimestampDay } from '../../../utils/date';
import { formStatuses } from '../../../constants/enums';

import '../cases.scss';

const CaseShippingInfo = ({
  activeCase,
  tenantColor,
  editable,
  caseForms = [],
  usageNotes = [],
  onFormsAlertClick,
  onUsageNoteAlertClick,
}) => {
  const dispatch = useDispatch();
  const { collectionDate, collectionNote = '', id } = activeCase;

  const [open, toggleModal] = useState(false);
  const [loading, setLoading] = useState(false);

  const submittedForms = useMemo(() => {
    return caseForms.filter((form) => form.status === formStatuses.SUBMITTED);
  }, [caseForms]);

  const hasUsageNotes = useMemo(() => {
    return usageNotes.some((note) => !!note?.note?.trim());
  }, [usageNotes]);

  const showAlerts = !!submittedForms.length || hasUsageNotes;

  const onSubmit = async (date, notes) => {
    if (date) {
      try {
        setLoading(true);
        const doc = { collectionDate: fromMomentToTimestampDay(date), collectionNote: notes || '' };
        await dispatch(simpleUpdateCase(id, doc));
        await dispatch(getCase(id));
        toggleModal(false);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    }
  };

  const onRevert = async () => {
    try {
      const doc = { collectionDate: null };
      await dispatch(simpleUpdateCase(id, doc));
      await dispatch(getCase(id));
      toggleModal(false);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className='case-notes__container'>
      <div className='d-flex m-b-lg space-between'>
        <div className='d-flex'>
          <InfoIcon color='var(--app-text)' />
          <div className='font-size-bg font-bold m-l-md'>
            Shipping Info
          </div>
        </div>

        {!!collectionDate && (
          <div className='d-flex'>
            <CheckIcon color="#4AB026" />
            <div className='font-size-bg font-bold m-l-md' style={{ color: "#4AB026" }}>
              Collection Scheduled
            </div>
          </div>
        )}
      </div>

      {collectionDate ? (
        <div>
          <ModalSelectInput
            onClick={() => {
              if (editable) {
                toggleModal(true)
              }
            }}
            label='Edit Date'
            icon='date'
            values={[`Collection on ${moment(collectionDate).format('MMM Do YYYY')}`]}
            backgroundColor='var(--app-surface-muted)'
          />
        </div>
      ) : (
        <div className='modal-select align-center' style={{ background: 'var(--app-surface-muted)' }}>
          <div className='font-bold'>Collection Date</div>
          {editable && (
            <div className='font-bold pointer' style={{ color: tenantColor }} onClick={() => toggleModal(true)}>Confirm Date</div>
          )}
        </div>
      )}

      {!!collectionNote && (
        <div className='m-t-lg'>
          <div className='field__title'>Collection Note</div>
          <Input
            value={collectionNote}
            type='textarea'
            disabled
            backgroundColor='var(--app-surface-muted)'
            rows={2}
            marginBottom={0}
          />
        </div>
      )}

      {showAlerts && (
        <div className='case-shipping-alerts'>
          {!!submittedForms.length && (
            <div
              className={`case-shipping-alert case-shipping-alert--form ${onFormsAlertClick ? 'pointer' : ''}`}
              onClick={onFormsAlertClick}
            >
              <WarningIcon className='case-shipping-alert__icon' />
              <div>
                <div className='case-shipping-alert__title'>
                  {submittedForms.length === 1 ? 'Form Submitted' : `${submittedForms.length} Forms Submitted`}
                </div>
                {onFormsAlertClick && (
                  <div className='case-shipping-alert__link'>View More details</div>
                )}
              </div>
            </div>
          )}

          {hasUsageNotes && (
            <div
              className={`case-shipping-alert case-shipping-alert--usage ${onUsageNoteAlertClick ? 'pointer' : ''}`}
              onClick={onUsageNoteAlertClick}
            >
              <WarningIcon className='case-shipping-alert__icon' />
              <div>
                <div className='case-shipping-alert__title'>Usage Note Added</div>
                {onUsageNoteAlertClick && (
                  <div className='case-shipping-alert__link'>View More details</div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      <CaseShippingInfoModal
        open={open}
        onClose={() => toggleModal(false)}
        onSubmit={onSubmit}
        onRevert={onRevert}
        loading={loading}
        initialValues={collectionDate ? { collectionDate, collectionNote } : { collectionNote: collectionNote || '' }}
      />
    </div>
  );
};

export default CaseShippingInfo;
